/**
 * Client API tipizzato per il backend RENTRI Suite.
 * Il prefix /api/v1 e' configurato in vite.config (proxy verso :8000).
 */

const BASE = "/api/v1";

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `API Error ${status}`);
  }
}

/** Messaggio leggibile da errori API (detail FastAPI o fallback). */
export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = error.body;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (detail && typeof detail === "object" && "detail" in detail) {
      const d = (detail as { detail: unknown }).detail;
      if (typeof d === "string" && d.trim()) return d;
    }
    if (error.status === 400) {
      return "File o password non validi. Controlla il file P12 e la password del certificato.";
    }
    if (error.status === 428) {
      return "Carica il certificato P12 in Impostazioni prima di usare le funzioni RENTRI.";
    }
    if (error.status === 402) {
      const d = detail && typeof detail === "object" && "detail" in detail
        ? (detail as { detail: unknown }).detail
        : detail;
      if (d && typeof d === "object" && "message" in d && typeof (d as { message: string }).message === "string") {
        return (d as { message: string }).message;
      }
      return "Abbonamento o pacchetto CER richiesto.";
    }
    return `Errore del server (${error.status})`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

import { buildAuthHeaders } from "@/lib/authHeaders";

async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  opts: { headers?: Record<string, string>; isForm?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...(await buildAuthHeaders()),
    ...(opts.headers ?? {}),
  };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (opts.isForm && body instanceof FormData) {
      payload = body;
    } else {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
  }
  const res = await fetch(`${BASE}${path}`, { method, headers, body: payload });
  const text = await res.text();
  let parsed: unknown = text;
  if (text) {
    try { parsed = JSON.parse(text); } catch { /* keep text */ }
  }
  if (!res.ok) {
    throw new ApiError(res.status, parsed, `${method} ${path} -> ${res.status}`);
  }
  return parsed as T;
}

/** Download autenticato (blob) — per CSV/PDF senza window.open. */
export async function downloadAuthenticated(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    headers: await buildAuthHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = text;
    try { parsed = JSON.parse(text); } catch { /* */ }
    throw new ApiError(res.status, parsed, `GET ${path} -> ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T = unknown>(p: string) => request<T>("GET", p),
  post: <T = unknown>(p: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>("POST", p, body, { headers }),
  put: <T = unknown>(p: string, body?: unknown) => request<T>("PUT", p, body),
  patch: <T = unknown>(p: string, body?: unknown) => request<T>("PATCH", p, body),
  delete: <T = unknown>(p: string) => request<T>("DELETE", p),
  postForm: <T = unknown>(p: string, form: FormData) =>
    request<T>("POST", p, form, { isForm: true }),
};

// --- Endpoint helpers tipizzati ---

export interface JobItem {
  id: string;
  job_type: string;
  status: string;
  rentri_transaction_id: string | null;
  attempts: number;
  next_poll_at: string | null;
  created_at: string;
  updated_at: string;
  result_payload: Record<string, unknown> | null;
  error: string | null;
  correlation_id: string | null;
}

export interface SigningStatus {
  configured?: boolean;
  canUseApp?: boolean;
  nominaConservazione?: boolean;
  contrattoArt28?: boolean;
  pendingNomina?: boolean;
  pendingContratto?: boolean;
  error?: string;
}

export interface SigningTemplateData {
  nome_azienda: string;
  indirizzo: string;
  piva: string;
  nome_rappresentante: string;
}

export interface AziendaInfo {
  id: string;
  ragione_sociale: string;
  piva: string | null;
  email_firma: string | null;
  status: "pending_payment" | "active" | "deactivating" | "removed";
}

export interface BillingStatus {
  /** L'utente loggato è un gestore multi azienda. */
  is_gestore: boolean;
  /** Email del gestore quando si sta lavorando dentro un'azienda gestita. */
  managed_by: string | null;
  azienda: AziendaInfo | null;
  billing_disabled: boolean;
  subscription_active: boolean;
  subscription_status: string | null;
  current_period_end: string | null;
  cer_slots: number;
  cer_used: number;
  cer_slots_remaining: number;
  movimenti_limit: number;
  xfir_limit: number;
  movimenti_used: number;
  xfir_used: number;
  signing: SigningStatus;
  can_use_app: boolean;
}

/** Buono Stripe monouso per attivare gratis Mar.te RENTRI Base (clienti Suite paganti). */
export interface BaseVoucher {
  eligible: boolean;
  reason: "not_configured" | "billing_disabled" | "expired" | "gestore_profile" | "no_subscription" | null;
  status: "issued" | "redeemed" | null;
  code: string | null;
  /** Link diretto a RENTRI Base con lo sconto pre-applicato. */
  link: string | null;
  issued_at: string | null;
  redeemed_at: string | null;
  expires_at: string | null;
  /** Ultimo giorno utile, formato italiano (es. 31/12/2026). */
  deadline: string;
  base_app_url: string;
  price_label: string;
  savings_label: string;
}

export interface Scaglione {
  da: number;
  a: number | null;
  prezzo_netto_cents: number;
  prezzo_lordo_cents?: number;
}

export interface GestoreAzienda extends AziendaInfo {
  active_until: string | null;
  da_fare: { certificato: boolean; firme: boolean; cer_over_quota: number };
}

export interface GestoreOverview {
  is_gestore: boolean;
  aziende: GestoreAzienda[];
  riepilogo: {
    aziende_attive: number;
    aziende_in_disattivazione: number;
    scaglione: Scaglione | null;
    scadenza_comune: string | null;
    subscription_status: string | null;
    rinnovo_stimato_netto_cents: number;
    scaglioni: Scaglione[];
    max_aziende: number;
    contatto_offerte: string;
  };
}

export interface AdminUserRow {
  id: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  is_gestore: boolean;
  is_azienda: boolean;
  managed_by: string | null;
  azienda_id: string | null;
  ragione_sociale: string | null;
  azienda_status: string | null;
  last_login_at: string | null;
}

export interface AdminGestore {
  email: string;
  aziende: number;
  attive: number;
  scaglione: Scaglione | null;
  subscription: string | null;
  subscription_status: string | null;
  scadenza: string | null;
  elenco: {
    id: string;
    account_email: string;
    ragione_sociale: string;
    piva: string | null;
    email_firma: string | null;
    status: string;
  }[];
}

export interface GestorePreventivo {
  kind: "multi_first" | "multi_add";
  aziende_attuali: number;
  aziende_totali: number;
  scaglione: Scaglione;
  cambia_scaglione: boolean;
  giorni_rimanenti: number | null;
  scadenza_comune: string | null;
  imponibile_cents: number;
  totale_cents: number;
  rinnovo_annuo_netto_cents: number;
}

export interface NuovaAziendaIn {
  ragione_sociale: string;
  piva?: string | null;
  email_firma?: string | null;
}

export interface CerQuotaStatus {
  subscription_active: boolean;
  cer_slots: number;
  cer_pack_slots: number;
  cer_used_effective: number;
  cer_over_quota: number;
  packs_needed: number;
}

export interface AuditItem {
  id: string;
  user_email: string | null;
  correlation_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  method: string | null;
  path: string | null;
  response_status: number | null;
  rentri_status: number | null;
  error: string | null;
  created_at: string;
}

export interface DraftItem {
  id: string;
  doc_type: "register" | "movement" | "fir" | "fir_attachment";
  parent_id: string | null;
  title: string | null;
  payload: unknown;
  status: "draft" | "ready" | "sent" | "sent_failed";
  rentri_response: unknown;
  rentri_resource_id: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactItem {
  id: string;
  role: string;
  denominazione: string;
  codice_fiscale: string | null;
  partita_iva: string | null;
  rentri_iscrizione: string | null;
  indirizzo: string | null;
  comune: string | null;
  provincia: string | null;
  cap: string | null;
  nazione: string | null;
  notes: string | null;
  rentri_check_at: string | null;
  rentri_check_result: unknown;
  use_count: number;
  last_used_at: string | null;
  created_at: string;
}

export interface CertificateItem {
  id: string;
  label: string;
  subject_cn: string | null;
  issuer_cn: string | null;
  serial_hex: string | null;
  subject_cf: string | null;
  rentri_iscrizione: string | null;
  not_before: string | null;
  not_after: string | null;
  expired: boolean;
  created_at: string;
}

export interface ValidationIssue {
  field: string; code: string; message: string; severity: "error" | "warning";
}
export interface ValidationResult {
  valid: boolean; issues: ValidationIssue[];
}

export interface ArchiveDocumentRow {
  id: string;
  doc_kind: "registro_xml" | "xfir_xml";
  reference_id: string;
  status: "pending" | "archived" | "failed";
  trigger: string;
  content_sha256: string | null;
  size_bytes: number | null;
  s3_key: string | null;
  error: string | null;
  created_at: string | null;
  conservation_status: "sent" | "archived" | "failed" | "skipped" | "non_rentri" | null;
  conservation_sent_at: string | null;
  conservation_archived_at: string | null;
  conservation_file_name: string | null;
  conservation_error: string | null;
}

export interface ConservationStatusRow extends ArchiveDocumentRow {
  dip_available: boolean;
  dip_file_name: string | null;
  dip_size: number | null;
}

export interface DipLink {
  url: string;
  file_name: string;
  size: number | null;
}

export const endpoints = {
  health: () => api.get<{ status: string; checks?: Record<string, string> }>("/health/ready"),
  rentriHealth: () =>
    api.get<{ status: string; user?: string; detail?: string }>("/health/rentri"),
  sync: {
    runs: () =>
      api.get<
        {
          id: string;
          direction: string;
          resource: string;
          status: string;
          stats: Record<string, unknown> | null;
          error: string | null;
          started_at: string;
          finished_at: string | null;
        }[]
      >("/sync/runs"),
    pullAll: (body: {
      num_iscr: string;
      num_iscr_sito: string;
      registro_id?: string;
    }) => api.post<Record<string, unknown>>("/sync/pull", body),
  },
  metrics: () => api.get<{
    jobs_by_status: Record<string, number>;
    drafts_by_status: Record<string, number>;
    audit_top_actions: { action: string; count: number }[];
  }>("/metrics/"),
  jobs: () => api.get<JobItem[]>("/jobs/"),
  pollJob: (id: string) => api.post<JobItem>(`/jobs/${id}/poll`),
  audit: (params?: { resource_type?: string; resource_id?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.resource_type) q.set("resource_type", params.resource_type);
    if (params?.resource_id) q.set("resource_id", params.resource_id);
    if (params?.limit) q.set("limit", String(params.limit));
    return api.get<{ items: AuditItem[]; limit: number; offset: number }>(`/audit/?${q}`);
  },
  drafts: {
    list: (params?: { doc_type?: string; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.doc_type) q.set("doc_type", params.doc_type);
      if (params?.status) q.set("status", params.status);
      return api.get<DraftItem[]>(`/drafts/?${q}`);
    },
    create: (b: { doc_type: string; parent_id?: string; title?: string; payload: unknown }) =>
      api.post<DraftItem>("/drafts/", b),
    get: (id: string) => api.get<DraftItem>(`/drafts/${id}`),
    update: (id: string, b: { title?: string; payload?: unknown; status?: string }) =>
      api.put<DraftItem>(`/drafts/${id}`, b),
    remove: (id: string) => api.delete(`/drafts/${id}`),
    send: (id: string) => api.post<{ draft: DraftItem; rentri_status: number; rentri_body: unknown }>(`/drafts/${id}/send`),
  },
  contacts: {
    list: (params?: { role?: string; q?: string }) => {
      const qs = new URLSearchParams();
      if (params?.role) qs.set("role", params.role);
      if (params?.q) qs.set("q", params.q);
      return api.get<ContactItem[]>(`/contacts/?${qs}`);
    },
    create: (b: Partial<ContactItem>) => api.post<ContactItem>("/contacts/", b),
    update: (id: string, b: Partial<ContactItem>) => api.put<ContactItem>(`/contacts/${id}`, b),
    remove: (id: string) => api.delete(`/contacts/${id}`),
    verify: (id: string) => api.post<{ rentri_status: number; result: unknown }>(`/contacts/${id}/verify-rentri`),
    touch: (id: string) => api.post(`/contacts/${id}/touch`),
    sites: {
      list: (contactId: string) =>
        api.get<{ id: string; num_iscr_sito: string; label: string | null }[]>(
          `/contacts/${contactId}/sites`,
        ),
      add: (contactId: string, body: { num_iscr_sito: string; label?: string }) =>
        api.post(`/contacts/${contactId}/sites`, body),
      remove: (contactId: string, siteId: string) =>
        api.delete(`/contacts/${contactId}/sites/${siteId}`),
    },
  },
  movimenti: {
    normalize: (body: Record<string, unknown>) =>
      api.post<{ items: unknown[]; count: number }>("/movimenti/normalize", body),
  },
  cerCatalog: {
    list: () =>
      api.get<
        {
          id: string;
          codice_eer: string;
          descrizione: string | null;
          stato_fisico: string | null;
          provenienza: string | null;
          scadenza_analisi: string | null;
          is_default: boolean;
          hp_default: string[] | null;
          created_at?: string;
          updated_at?: string;
        }[]
      >("/cer-catalog/"),
    create: (body: Record<string, unknown>) => api.post("/cer-catalog/", body),
    update: (id: string, body: Record<string, unknown>) => api.put(`/cer-catalog/${id}`, body),
    remove: (id: string) => api.delete(`/cer-catalog/${id}`),
  },
  settings: {
    preferences: () => api.get<{ preferences: Record<string, unknown> }>("/settings/preferences"),
    savePreferences: (preferences: Record<string, unknown>) =>
      api.put("/settings/preferences", { preferences }),
  },
  certificates: {
    list: () => api.get<CertificateItem[]>("/certificati/"),
    upload: (file: File, label: string, password: string) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      fd.append("password", password);
      return api.postForm<CertificateItem>("/certificati/", fd);
    },
    remove: (id: string) => api.delete(`/certificati/${id}`),
  },
  search: (q: string) => api.get<{
    drafts: { id: string; doc_type: string; title: string | null; parent_id: string | null; status: string }[];
    contacts: { id: string; role: string; denominazione: string; rentri_iscrizione: string | null }[];
    rentri_cache: { id: string; resource_type: string; resource_key: string }[];
    audit: { id: string; action: string; resource_id: string | null; created_at: string }[];
  }>(`/search/?q=${encodeURIComponent(q)}`),
  // RENTRI passthrough
  anagrafiche: {
    operatori: (q?: string) =>
      api.get<
        Array<{
          num_iscr?: string;
          identificativo?: string;
          denominazione?: string;
          forma_giuridica_id?: string;
          piva?: string;
          cciaarea?: string;
          nrea?: string;
          data_iscrizione_ri?: string;
          stato_impresa_ri?: string;
          pec?: string;
          citta_sl?: string;
          indirizzo_sl?: string;
          civico_sl?: string;
          capsl?: string;
          provincia_slid?: string;
          cognome_lr?: string;
          nome_lr?: string;
          cod_fisc_lr?: string;
          data_iscrizione?: string;
          profilo_soggetto?: string;
        }>
      >(`/anagrafiche/operatori${q ? `?q=${encodeURIComponent(q)}` : ""}`),
    controlloIscrizione: (numIscr: string) =>
      api.get<boolean>(`/anagrafiche/operatore/${numIscr}/controllo-iscrizione`),
    controlloAutorizzazioneAlbo: (numIscr: string) =>
      api.get<boolean>(`/anagrafiche/operatore/${numIscr}/controllo-autorizzazione-albo`),
    siti: (numIscr: string) => api.get(`/anagrafiche/operatore/${numIscr}/siti`),
    sito: (numIscr: string, sitoId: string) => api.get(`/anagrafiche/operatore/${numIscr}/siti/${sitoId}`),
    autorizzazioni: (numIscr: string, sitoId: string) =>
      api.get(`/anagrafiche/operatore/${numIscr}/siti/${sitoId}/autorizzazioni`),
    autorizzazioneAlbo: (numIscr: string) => api.get(`/anagrafiche/operatore/${numIscr}/autorizzazione-albo`),
  },
  codifiche: {
    lookup: (name: string) => api.get<unknown[]>(`/codifiche/lookup/${name}`),
  },
  registri: {
    lista: (numIscr: string, sitoId: string, view?: "enriched", source: "auto" | "mirror" | "live" = "auto") => {
      const q = new URLSearchParams({ source });
      if (view) q.set("view", view);
      return api.get(`/registri/operatore/${numIscr}/siti/${sitoId}/registri?${q}`);
    },
    get: (id: string) => api.get(`/registri/${id}`),
    apri: (payload: unknown, idemKey?: string) =>
      api.post("/registri/", payload, idemKey ? { "Idempotency-Key": idemKey } : undefined),
    modifica: (id: string, body: { descrizione?: string | null }) =>
      api.put<{ rentri_status: number; body: unknown }>(
        `/registri/${encodeURIComponent(id)}`,
        body,
      ),
    chiudi: (id: string) =>
      api.post<{ rentri_status: number; body: unknown }>(
        `/registri/${encodeURIComponent(id)}/chiudi`,
        {},
      ),
    // ---- Flusso "guidato" registri: bozza locale + invio user-friendly ----
    creaBozza: (
      payload: {
        num_iscr_sito: string;
        attivita: string[];
        attivita_rec_smalt?: string[];
        descrizione?: string;
      },
      title?: string,
    ) =>
      api.post<DraftItem>("/registri/bozza", { payload, title }),
    aggiornaBozza: (
      draftId: string,
      body: {
        payload?: {
          num_iscr_sito: string;
          attivita: string[];
          attivita_rec_smalt?: string[];
          descrizione?: string;
        };
        title?: string;
      },
    ) =>
      api.put<DraftItem>(
        `/registri/bozza/${encodeURIComponent(draftId)}`,
        body,
      ),
    eliminaBozza: (draftId: string) =>
      api.delete<{ status: string; id: string }>(
        `/registri/bozza/${encodeURIComponent(draftId)}`,
      ),
    inviaBozza: (draftId: string) =>
      api.post<{
        draft: DraftItem;
        rentri_status: number;
        rentri_body: unknown;
        already_sent?: boolean;
      }>(`/registri/bozza/${encodeURIComponent(draftId)}/invia`, {}),
    creaEInvia: (
      payload: {
        num_iscr_sito: string;
        attivita: string[];
        attivita_rec_smalt?: string[];
        descrizione?: string;
      },
      title?: string,
    ) =>
      api.post<{
        draft: DraftItem;
        rentri_status: number;
        rentri_body: unknown;
      }>("/registri/crea-e-invia", { payload, title }),
    getBozza: (draftId: string) =>
      api.get<DraftItem>(`/registri/bozza/${encodeURIComponent(draftId)}`),
    movimenti: (id: string, page = 1, view?: "enriched", source: "auto" | "mirror" | "live" = "auto") => {
      const q = new URLSearchParams({ page: String(page), source });
      if (view) q.set("view", view);
      return api.get(`/registri/${id}/movimenti?${q}`);
    },
    countMovimenti: (id: string, source: "auto" | "mirror" | "live" = "auto") =>
      api.get<number>(
        `/registri/${encodeURIComponent(id)}/movimenti/count?source=${encodeURIComponent(source)}`,
      ),
    creaMovimenti: (id: string, items: unknown[], idemKey: string) =>
      api.post(`/registri/${id}/movimenti`, items, { "Idempotency-Key": idemKey }),
    // ---- Flusso user-friendly guidato: bozze + invio sotto /registri ----
    creaBozzaMovimento: (id: string, payload: unknown, title?: string) =>
      api.post<DraftItem>(`/registri/${encodeURIComponent(id)}/movimenti-bozza`, {
        payload,
        title,
      }),
    aggiornaBozzaMovimento: (
      id: string,
      draftId: string,
      body: { payload?: unknown; title?: string },
    ) =>
      api.put<DraftItem>(
        `/registri/${encodeURIComponent(id)}/movimenti-bozza/${encodeURIComponent(draftId)}`,
        body,
      ),
    eliminaBozzaMovimento: (id: string, draftId: string) =>
      api.delete<{ status: string; id: string }>(
        `/registri/${encodeURIComponent(id)}/movimenti-bozza/${encodeURIComponent(draftId)}`,
      ),
    inviaMovimento: (id: string, draftId: string) =>
      api.post<{
        draft: DraftItem;
        rentri_status: number;
        rentri_body: unknown;
        already_sent?: boolean;
      }>(
        `/registri/${encodeURIComponent(id)}/movimenti-bozza/${encodeURIComponent(draftId)}/invia`,
      ),
    listMovimentiBozza: (id: string) =>
      api.get<DraftItem[]>(`/registri/${encodeURIComponent(id)}/movimenti-bozza`),
    creaEInviaMovimento: (id: string, payload: unknown, title?: string) =>
      api.post<{ draft: DraftItem; rentri_status: number; rentri_body: unknown }>(
        `/registri/${encodeURIComponent(id)}/movimenti-crea-e-invia`,
        { payload, title },
      ),
  },
  formulari: {
    lista: (
      params?: {
        num_iscr_sito?: string;
        identificativo_soggetto?: string;
        source?: "auto" | "mirror" | "live";
      },
    ) => {
      const q = new URLSearchParams();
      q.set("source", params?.source ?? "auto");
      if (params?.num_iscr_sito) q.set("num_iscr_sito", params.num_iscr_sito);
      if (params?.identificativo_soggetto) q.set("identificativo_soggetto", params.identificativo_soggetto);
      return api.get(`/formulari/?${q}`);
    },
    get: (n: string) => api.get(`/formulari/${encodeURIComponent(n)}`),
    trasmissioni: (numIscrSito: string) =>
      api.get(`/formulari/trasmissioni/operatore/${encodeURIComponent(numIscrSito)}`),
    crea: (payload: unknown, idemKey: string) =>
      api.post("/formulari/", payload, { "Idempotency-Key": idemKey }),
    azioni: (n: string) => api.get(`/formulari/${n}/azioni`),
    stato: (n: string) => api.get(`/formulari/${n}/stato`),
  },
  vidimazione: {
    lista: () => api.get("/vidimazione/"),
    verifica: (numero: string) => api.get(`/vidimazione/verifica/${encodeURIComponent(numero.trim())}`),
    richiedi: (codiceBlocco: string, payload: unknown = {}, idemKey?: string) =>
      api.post<{ rentri_status: number; body: unknown }>(
        `/vidimazione/${encodeURIComponent(codiceBlocco)}`,
        payload,
        idemKey ? { "Idempotency-Key": idemKey } : undefined,
      ),
    dettaglio: (codiceBlocco: string, progressivo: number) =>
      api.get(`/vidimazione/${encodeURIComponent(codiceBlocco)}/${progressivo}`),
    pdf: (codiceBlocco: string, progressivo: number, filename: string) =>
      downloadAuthenticated(
        `/vidimazione/${encodeURIComponent(codiceBlocco)}/${progressivo}/pdf`,
        filename,
      ),
    transazioneStatus: (txId: string) => api.get(`/vidimazione/transazione/${encodeURIComponent(txId)}/status`),
    transazioneResult: (txId: string) => api.get(`/vidimazione/transazione/${encodeURIComponent(txId)}/result`),
  },
  validate: {
    eer: (eer: string) => api.post<ValidationResult>("/validate/eer", { eer }),
    movimento: (m: unknown) => api.post<ValidationResult>("/validate/movimento", m),
    fir: (m: unknown) => api.post<ValidationResult>("/validate/fir", m),
    registroApertura: (m: unknown) => api.post<ValidationResult>("/validate/registro-apertura", m),
  },
  giacenze: {
    perRegistro: (registroId: string) => api.get<{
      registro_id: string;
      totale_movimenti: number;
      totale_giacenza_kg: number;
      rows: { eer: string; carico_kg: number; scarico_kg: number; giacenza_kg: number; n_carichi: number; n_scarichi: number }[];
    }>(`/giacenze/registro/${registroId}`),
  },
  scadenze: {
    certificati: (daysWarning = 60) => api.get<{
      items: { id: string; label: string; subject_cn: string | null; rentri_iscrizione: string | null; not_after: string | null; days_to_expire: number; status: "expired" | "warning" | "ok" }[];
      summary: { total: number; expired: number; warning: number; ok: number };
      days_warning: number;
    }>(`/scadenze/certificati?days_warning=${daysWarning}`),
  },
  analytics: {
    trend: () => api.get<{ window_days: number; items: { day: string; count: number }[] }>("/analytics/trend"),
    topResources: (limit = 10) => api.get<{ items: { resource_type: string; resource_id: string; count: number }[] }>(`/analytics/top-resources?limit=${limit}`),
    jobsRate: () => api.get<{ total: number; success: number; failed: number; pending: number; success_rate: number | null; by_status: Record<string, number> }>("/analytics/jobs-rate"),
  },
  mud: {
    csvUrl: (registroId: string, anno: number) => `/mud/${registroId}.csv?anno=${anno}`,
    downloadCsv: (registroId: string, anno: number) =>
      downloadAuthenticated(`/mud/${registroId}.csv?anno=${anno}`, `mud-${registroId}-${anno}.csv`),
  },
  archive: {
    list: (params?: { doc_kind?: string; reference_id?: string; limit?: number }) => {
      const q = new URLSearchParams();
      if (params?.doc_kind) q.set("doc_kind", params.doc_kind);
      if (params?.reference_id) q.set("reference_id", params.reference_id);
      if (params?.limit != null) q.set("limit", String(params.limit));
      const qs = q.toString();
      return api.get<ArchiveDocumentRow[]>(`/archive/${qs ? `?${qs}` : ""}`);
    },
    snapshotRegistro: (identificativo: string) =>
      api.post<ArchiveDocumentRow>(
        `/archive/registri/${encodeURIComponent(identificativo)}/snapshot`,
        {},
      ),
    snapshotXfir: (numeroFir: string) =>
      api.post<ArchiveDocumentRow>(
        `/archive/formulari/${encodeURIComponent(numeroFir)}/xfir`,
        {},
      ),
    downloadFile: (archiveId: string, filename: string) =>
      downloadAuthenticated(
        `/archive/${encodeURIComponent(archiveId)}/download`,
        filename,
      ),
    conservationStatus: (archiveId: string) =>
      api.get<ConservationStatusRow>(
        `/archive/${encodeURIComponent(archiveId)}/conservation-status`,
      ),
    /** Avvia il download del DIP dal link fornito da MarteWeb. */
    downloadDip: async (archiveId: string) => {
      const dip = await api.get<DipLink>(`/archive/${encodeURIComponent(archiveId)}/dip`);
      const a = document.createElement("a");
      a.href = dip.url;
      a.download = dip.file_name;
      a.rel = "noopener";
      a.click();
      return dip;
    },
  },
  users: {
    list: () => api.get<{ items: AdminUserRow[] }>("/users/"),
    gestori: () => api.get<{ items: AdminGestore[]; count: number }>("/users/gestori"),
  },
  gestore: {
    attiva: () => api.post<{ is_gestore: boolean }>("/gestore/attiva", {}),
    aziende: () => api.get<GestoreOverview>("/gestore/aziende"),
    preventivo: (n_aziende: number) =>
      api.post<GestorePreventivo>("/gestore/aziende/preventivo", { n_aziende }),
    checkout: (body: { aziende: NuovaAziendaIn[]; success_url?: string; cancel_url?: string }) =>
      api.post<{ url: string }>("/gestore/checkout", body),
    disattiva: (id: string) => api.post<GestoreAzienda>(`/gestore/aziende/${id}/disattiva`, {}),
    riattiva: (id: string) => api.post<GestoreAzienda>(`/gestore/aziende/${id}/riattiva`, {}),
    aggiorna: (id: string, body: Partial<NuovaAziendaIn>) =>
      api.patch<GestoreAzienda>(`/gestore/aziende/${id}`, body),
  },
  billing: {
    status: () => api.get<BillingStatus>("/billing/status"),
    sync: () => api.post<BillingStatus>("/billing/sync", {}),
    cerQuota: () => api.get<CerQuotaStatus>("/billing/cer-quota"),
    checkout: (body: { kind: "suite_annual" | "cer_pack"; success_url?: string; cancel_url?: string }) =>
      api.post<{ url: string }>("/billing/checkout", body),
    portal: (return_url?: string) =>
      api.post<{ url: string }>("/billing/portal", { return_url: return_url ?? null }),
    baseVoucher: () => api.get<BaseVoucher>("/billing/base-voucher"),
    issueBaseVoucher: () => api.post<BaseVoucher>("/billing/base-voucher", {}),
    signingStatus: () => api.get<SigningStatus>("/billing/signing-status"),
    signingRequests: (template_data: SigningTemplateData) =>
      api.post<{ success: boolean; message: string }>("/billing/signing-requests", { template_data }),
  },
  autista: {
    firma: (numero: string, body: {
      autista_nome: string;
      autista_cf?: string;
      targa_motrice?: string;
      targa_rimorchio?: string;
      note?: string;
      firma_png_base64: string;
      geo_lat?: number;
      geo_lon?: number;
    }) => api.post<{ id: string; fir_numero: string; autista: string; saved_at: string; status: string }>(`/autista/fir/${numero}/firma`, body),
    firmePerFir: (numero: string) => api.get<{
      fir_numero: string;
      items: { id: string; autista: string; targa_motrice: string | null; targa_rimorchio: string | null; geo: { lat: number | null; lon: number | null }; note: string | null; created_at: string; firma_preview: string }[];
    }>(`/autista/fir/${numero}/firme`),
    firmeAll: (limit = 50) => api.get<{ items: { id: string; fir_numero: string; autista: string; created_at: string }[] }>(`/autista/firme?limit=${limit}`),
    firma_get: (id: string) => api.get<{ id: string; fir_numero: string; payload: Record<string, unknown>; created_at: string }>(`/autista/firma/${id}`),
  },
};
